import React, { useEffect, useState } from 'react'
import PqrSectionModal from '../pqrPreview/PqrSectionModal'

export interface WpsIdentifiersValue {
  wpsNumber: string
  revision: string
  application: string
  equipmentDescription: string
}

interface Props {
  open: boolean
  initial: WpsIdentifiersValue
  onApply: (next: WpsIdentifiersValue) => void
  onCancel: () => void
}

/**
 * WPS Identifiers modal — mirrors `pqrPreview/IdentifiersModal.tsx` but
 * edits the four `sections.wps`-level fields the WPS PDF top band /
 * top metadata block consumes:
 *
 *   - `wpsNumber`           ← top band identifier badge
 *   - `revision`            ← top band identifier badge
 *   - `application`         ← top meta row 0 right cell
 *   - `equipmentDescription`← top meta row 2 left cell
 *
 * The modal owns a local working copy keyed off `initial`. Apply
 * commits via `onApply`; Cancel discards (with the shell's standard
 * inline dirty-confirm guard).
 *
 * Validation is intentionally absent — slice 02 lets users park
 * drafts with blank fields (the WPS preview toolbar's "Save &
 * Generate" button is what gates required `wpsNumber` / `revision`).
 */
export default function WpsIdentifiersModal({
  open,
  initial,
  onApply,
  onCancel
}: Props) {
  const [draft, setDraft] = useState<WpsIdentifiersValue>(initial)

  // Reset the working copy whenever the modal opens with a fresh
  // snapshot, so reopening after Cancel always starts from the
  // current staged value.
  useEffect(() => {
    if (open) setDraft(initial)
  }, [
    open,
    initial.wpsNumber,
    initial.revision,
    initial.application,
    initial.equipmentDescription
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty =
    draft.wpsNumber !== initial.wpsNumber ||
    draft.revision !== initial.revision ||
    draft.application !== initial.application ||
    draft.equipmentDescription !== initial.equipmentDescription

  return (
    <PqrSectionModal
      open={open}
      title="Edit WPS identifiers"
      isModalDirty={isDirty}
      onApply={() => onApply(draft)}
      onCancel={onCancel}
      panelMaxWidthClassName="max-w-2xl"
    >
      <div className="grid grid-cols-2 gap-4">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">WPS No.</span>
          <input
            type="text"
            value={draft.wpsNumber}
            onChange={(e) => setDraft((d) => ({ ...d, wpsNumber: e.target.value }))}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            autoFocus
            data-testid="wps-number-input"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Rev.</span>
          <input
            type="text"
            value={draft.revision}
            onChange={(e) => setDraft((d) => ({ ...d, revision: e.target.value }))}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            data-testid="wps-revision-input"
          />
        </label>
        <label className="text-sm col-span-2">
          <span className="mb-1 block font-medium text-gray-700">Application</span>
          <input
            type="text"
            value={draft.application}
            onChange={(e) => setDraft((d) => ({ ...d, application: e.target.value }))}
            placeholder="Pipeline Welding"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            data-testid="wps-application-input"
          />
        </label>
        <label className="text-sm col-span-2">
          <span className="mb-1 block font-medium text-gray-700">
            Equipment Description
          </span>
          <textarea
            value={draft.equipmentDescription}
            onChange={(e) =>
              setDraft((d) => ({ ...d, equipmentDescription: e.target.value }))
            }
            rows={2}
            placeholder="CRC-Evans IWM / P-600Z / M-300"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-brand-red-500 focus:outline-none focus:ring-1 focus:ring-brand-red-500"
            data-testid="wps-equipment-input"
          />
        </label>
      </div>
      <p className="text-xs text-gray-500 mt-4">
        Changes are staged in the preview. Click <strong>Save</strong> or{' '}
        <strong>Save &amp; Generate Report</strong> on the page header to persist
        them.
      </p>
    </PqrSectionModal>
  )
}
